import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Search, Music, Disc3, TrendingUp, TrendingDown, Heart, Settings, Database, Plus, Filter, Trash2, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Dashboard() {
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [compatibleTracks, setCompatibleTracks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [bpmFilter, setBpmFilter] = useState('exact');
  const [keyFilter, setKeyFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [libraryPath, setLibraryPath] = useState('');
  const [showAddTrack, setShowAddTrack] = useState(false);
  const [newTrack, setNewTrack] = useState({
    title: '',
    artist: '',
    bpm: '',
    key: '',
    genre: '',
    file_path: ''
  });
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [webSearchLoading, setWebSearchLoading] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [searchSource, setSearchSource] = useState('spotify');
  const [searchType, setSearchType] = useState('tracks');
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedWebTrack, setSelectedWebTrack] = useState(null);
  const [localSuggestions, setLocalSuggestions] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [customYear, setCustomYear] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [showSongListModal, setShowSongListModal] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [previousSearchResults, setPreviousSearchResults] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [playlistResults, setPlaylistResults] = useState([]);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [playlistInfo, setPlaylistInfo] = useState(null); // Store playlist name/owner

  useEffect(() => {
    loadTracks();
    loadStats();
    loadFavorites();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined') return {};
    return { Authorization: `Bearer ${token}` };
  };

  const loadTracks = async () => {
    try {
      const response = await axios.get(`${API}/tracks`, {
        headers: getAuthHeader()
      });
      setTracks(response.data);
    } catch (error) {
      console.error('Error loading tracks:', error);
      if (error.code !== 'ERR_NETWORK') toast.error('Failed to load tracks');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: getAuthHeader()
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const handleTrackSelect = async (track) => {
    setSelectedTrack(track);
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/tracks/compatible`, {
        current_track_id: track.id,
        bpm_match: bpmFilter,
        key_match: keyFilter
      }, { headers: getAuthHeader() });
      
      setCompatibleTracks(response.data.compatible_tracks);
      toast.success(`Found ${response.data.count} compatible tracks`);
    } catch (error) {
      toast.error('Error finding compatible tracks');
    } finally {
      setLoading(false);
    }
  };

  const handleScanLibrary = async () => {
    if (!libraryPath.trim()) {
      toast.error('Please enter library path');
      return;
    }
    
    setLoading(true);
    const loadingToast = toast.loading('Scanning library... This may take a few minutes for large libraries');
    
    try {
      const response = await axios.post(`${API}/library/scan?library_path=${encodeURIComponent(libraryPath)}`);
      toast.dismiss(loadingToast);
      
      const tracksCount = response.data.scan?.tracks_count || 0;
      if (tracksCount > 0) {
        toast.success(`Library scanned! Imported ${tracksCount} tracks with BPM data`);
      } else {
        toast.warning('Library scanned but no tracks with BPM data were found. Make sure your audio files have BPM metadata.');
      }
      
      loadTracks();
      loadStats();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(`Error scanning library: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderSelect = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      // Get the path from the first file
      const firstFile = files[0];
      // Extract directory path (remove filename)
      const fullPath = firstFile.webkitRelativePath || firstFile.path || firstFile.name;
      const folderPath = fullPath.substring(0, fullPath.lastIndexOf('/'));
      
      // For display purposes, show the selected folder
      setLibraryPath(folderPath || firstFile.name);
      toast.success(`Selected folder: ${folderPath || firstFile.name}`);
    }
  };

  const handleAddTrack = async () => {
    if (!newTrack.title || !newTrack.artist || !newTrack.bpm || !newTrack.file_path) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/tracks`, {
        ...newTrack,
        bpm: parseFloat(newTrack.bpm)
      });
      
      toast.success('Track added successfully');
      setShowAddTrack(false);
      setNewTrack({ title: '', artist: '', bpm: '', key: '', genre: '', file_path: '' });
      loadTracks();
      loadStats();
    } catch (error) {
      toast.error('Error adding track');
    }
  };

  const handleSaveFavorite = async (track2) => {
    if (!selectedTrack) return;
    
    try {
      await axios.post(`${API}/favorites?track1_id=${selectedTrack.id}&track2_id=${track2.id}`);
      toast.success('Saved to favorites!');
      loadFavorites();
    } catch (error) {
      toast.error('Error saving favorite');
    }
  };

  const handleWebSearch = async (query = webSearchQuery) => {
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    setWebSearchLoading(true);
    setSelectedPlaylist(null);
    setCurrentOffset(0);
    setHasMore(true);
    
    try {
      // Build search query - add genre/year only if not already in query
      let searchQuery = query.trim();
      
      // Add genre if selected and not already in query
      if (selectedGenre !== 'all' && !searchQuery.toLowerCase().includes(selectedGenre.toLowerCase())) {
        searchQuery += ` ${selectedGenre}`;
      }
      
      // Add year if selected and not already in query
      const yearToUse = selectedYear === 'custom' ? customYear : selectedYear;
      if (selectedYear !== 'all' && selectedYear !== 'custom' && !searchQuery.includes(yearToUse)) {
        searchQuery += ` ${yearToUse}`;
      } else if (selectedYear === 'custom' && customYear && !searchQuery.includes(customYear)) {
        searchQuery += ` ${customYear}`;
      }
      
      setLastSearchQuery(searchQuery);
      
      if (searchType === 'playlists' && (searchSource === 'spotify' || searchSource === 'youtube')) {
        // Search for playlists
        let response;
        if (searchSource === 'spotify') {
          response = await axios.get(`${API}/search/spotify/playlists?query=${encodeURIComponent(searchQuery)}`);
        } else if (searchSource === 'youtube') {
          response = await axios.get(`${API}/search/youtube/playlists?query=${encodeURIComponent(searchQuery)}`);
        }
        setPlaylists(response.data.playlists);
        setWebSearchResults([]);
        toast.success(`Found ${response.data.count} playlists`);
      } else {
        // Search for tracks - NO GENRE FILTERING, let each service handle it
        let response;
        if (searchSource === 'spotify') {
          response = await axios.get(`${API}/search/spotify?query=${encodeURIComponent(searchQuery)}&limit=50&offset=0`);
          toast.success(`Found ${response.data.count} songs from Spotify`);
        } else if (searchSource === 'youtube') {
          response = await axios.get(`${API}/search/youtube?query=${encodeURIComponent(searchQuery)}&limit=100`);
          toast.success(`Found ${response.data.count}+ videos from YouTube (scroll for more)`);
        } else {
          response = await axios.get(`${API}/search/web?query=${encodeURIComponent(searchQuery)}`);
          toast.success(`Found ${response.data.count} songs from iTunes`);
        }
        
        // Don't filter by genre - let the search query handle it naturally
        setWebSearchResults(response.data.tracks);
        setPlaylists([]);
        // Set hasMore based on whether we got a full batch
        if (searchSource === 'youtube') {
          setHasMore(response.data.tracks.length >= 50); // YouTube can load more
        } else {
          setHasMore(response.data.tracks.length === 50);
        }
      }
    } catch (error) {
      toast.error('Error searching for songs');
      console.error('Search error:', error);
    } finally {
      setWebSearchLoading(false);
    }
  };

  const handleCuratedSearch = async (category) => {
    setWebSearchLoading(true);
    setSelectedPlaylist(null);
    setCurrentOffset(0);
    setHasMore(true);
    
    try {
      let response;
      if (searchSource === 'spotify') {
        response = await axios.get(`${API}/search/spotify/curated/${category}?limit=50&offset=0`);
        toast.success(`Found ${response.data.count} ${category} songs from Spotify`);
      } else if (searchSource === 'youtube') {
        response = await axios.get(`${API}/search/youtube?query=${encodeURIComponent(category)}&limit=100`);
        toast.success(`Found ${response.data.count}+ ${category} videos from YouTube`);
      } else {
        response = await axios.get(`${API}/search/curated/${encodeURIComponent(category)}`);
        toast.success(`Found ${response.data.count} ${category} songs from iTunes`);
      }
      setWebSearchResults(response.data.tracks);
      setWebSearchQuery(response.data.query);
      setLastSearchQuery(response.data.query);
      setPlaylists([]);
      setHasMore(response.data.tracks.length === 50);
    } catch (error) {
      toast.error('Error loading curated playlist');
      console.error('Curated search error:', error);
    } finally {
      setWebSearchLoading(false);
    }
  };

  const handlePlaylistSelect = async (playlist) => {
    // Save current search results before switching
    setPreviousSearchResults(webSearchResults);
    
    setSelectedPlaylist(playlist);
    setWebSearchLoading(true);
    
    try {
      let response;
      if (searchSource === 'spotify') {
        response = await axios.get(`${API}/search/spotify/playlist/${playlist.id}/tracks`);
      } else if (searchSource === 'youtube') {
        response = await axios.get(`${API}/search/youtube/playlist/${playlist.id}/tracks`);
      }
      setWebSearchResults(response.data.tracks);
      toast.success(`Loaded ${response.data.count} tracks from "${playlist.name}"`);
    } catch (error) {
      toast.error('Error loading playlist tracks');
    } finally {
      setWebSearchLoading(false);
    }
  };

  const handleTrendingSearch = async (genre) => {
    setWebSearchLoading(true);
    setSelectedPlaylist(null);
    setCurrentOffset(0);
    setHasMore(true);
    
    try {
      let response;
      if (searchSource === 'youtube') {
        response = await axios.get(`${API}/search/youtube?query=${encodeURIComponent(genre + ' trending hits')}&limit=100`);
        toast.success(`Loaded ${response.data.count}+ trending ${genre} videos from YouTube (scroll for more)!`);
      } else {
        response = await axios.get(`${API}/charts/trending?genre=${encodeURIComponent(genre)}&limit=50&offset=0`);
        toast.success(`Loaded ${response.data.count} trending ${genre} tracks from ${response.data.source === 'spotify' ? 'Spotify' : 'iTunes'}!`);
      }
      
      setWebSearchResults(response.data.tracks);
      setWebSearchQuery(`${genre} trending`);
      setLastSearchQuery(`trending:${genre}`);
      setPlaylists([]);
      setHasMore(response.data.tracks.length === 50);
    } catch (error) {
      toast.error('Error loading trending charts');
    } finally {
      setWebSearchLoading(false);
    }
  };

  const loadMoreResults = async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    const newOffset = currentOffset + 50;
    
    try {
      let response;
      let newItems = [];
      
      // Handle playlists mode
      if (searchType === 'playlists') {
        if (searchSource === 'spotify') {
          response = await axios.get(`${API}/search/spotify/playlists?query=${encodeURIComponent(lastSearchQuery)}&limit=50&offset=${newOffset}`);
          newItems = response.data.playlists || [];
        } else if (searchSource === 'youtube') {
          // YouTube playlists with search variations
          const searchVariations = [
            `${lastSearchQuery} playlist`,
            `${lastSearchQuery} mix playlist`,
            `${lastSearchQuery} best playlist`,
            `${lastSearchQuery} top playlist`
          ];
          const variation = searchVariations[Math.floor(currentOffset / 50) % searchVariations.length];
          response = await axios.get(`${API}/search/youtube/playlists?query=${encodeURIComponent(variation)}&limit=50`);
          newItems = (response.data.playlists || []).filter(
            newItem => !webSearchResults.some(existing => existing.id === newItem.id)
          );
        } else if (searchSource === 'itunes') {
          // iTunes doesn't have playlist search, disable
          setHasMore(false);
          setIsLoadingMore(false);
          return;
        }
      }
      // Handle tracks mode
      else {
        if (lastSearchQuery.startsWith('trending:')) {
          const genre = lastSearchQuery.replace('trending:', '');
          if (searchSource === 'youtube') {
            const searchVariations = [
              `${genre} trending hits`,
              `${genre} top songs`,
              `${genre} best tracks`,
              `${genre} 2024 hits`,
              `${genre} popular`
            ];
            const variation = searchVariations[Math.floor(currentOffset / 50) % searchVariations.length];
            response = await axios.get(`${API}/search/youtube?query=${encodeURIComponent(variation)}&limit=50`);
            newItems = (response.data.tracks || []).filter(
              newTrack => !webSearchResults.some(existing => existing.id === newTrack.id)
            );
          } else if (searchSource === 'spotify') {
            response = await axios.get(`${API}/charts/trending?genre=${genre}&limit=50&offset=${newOffset}`);
            newItems = response.data.tracks || [];
          } else {
            response = await axios.get(`${API}/search/web?query=${genre} trending&limit=50`);
            newItems = response.data.tracks || [];
          }
        } else if (searchSource === 'spotify') {
          response = await axios.get(`${API}/search/spotify?query=${encodeURIComponent(lastSearchQuery)}&limit=50&offset=${newOffset}`);
          newItems = response.data.tracks || [];
        } else if (searchSource === 'youtube') {
          // YouTube tracks with search variations
          const searchVariations = [
            lastSearchQuery,
            `${lastSearchQuery} official`,
            `${lastSearchQuery} audio`,
            `${lastSearchQuery} music video`,
            `${lastSearchQuery} lyric video`,
            `${lastSearchQuery} 2024`,
            `${lastSearchQuery} best`
          ];
          const variation = searchVariations[Math.floor(currentOffset / 50) % searchVariations.length];
          response = await axios.get(`${API}/search/youtube?query=${encodeURIComponent(variation)}&limit=50`);
          newItems = (response.data.tracks || []).filter(
            newTrack => !webSearchResults.some(existing => existing.id === newTrack.id)
          );
        } else if (searchSource === 'itunes') {
          // iTunes with offset
          response = await axios.get(`${API}/search/web?query=${encodeURIComponent(lastSearchQuery)}&limit=50&offset=${newOffset}`);
          newItems = (response.data.tracks || []).filter(
            newTrack => !webSearchResults.some(existing => existing.id === newTrack.id)
          );
        }
      }
      
      if (newItems.length > 0) {
        setWebSearchResults(prev => [...prev, ...newItems]);
        setCurrentOffset(newOffset);
        setHasMore(true); // Always try to load more
        toast.success(`Loaded ${newItems.length} more results!`);
      } else {
        setHasMore(false);
        toast.info('No more results available');
      }
    } catch (error) {
      console.error('Error loading more results:', error);
      toast.error('Failed to load more results');
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
    if (bottom && hasMore && !isLoadingMore) {
      loadMoreResults();
    }
  };

  const fetchPlaylistFromUrl = async () => {
    if (!playlistUrl.trim()) {
      toast.error('Please enter a playlist URL');
      return;
    }

    setPlaylistLoading(true);
    setPlaylistResults([]);
    setPlaylistInfo(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/search/playlist-url`, 
        { url: playlistUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.tracks) {
        setPlaylistResults(response.data.tracks);
        
        // Store playlist info if available
        if (response.data.playlist_name) {
          setPlaylistInfo({
            name: response.data.playlist_name,
            owner: response.data.playlist_owner || 'Unknown',
            source: response.data.source || 'unknown',
            count: response.data.count
          });
        }
        
        const source = response.data.source === 'spotify' ? 'Spotify' : 'YouTube';
        toast.success(`Found ${response.data.tracks.length} tracks from ${source} playlist!`);
      } else {
        toast.info('No tracks found in this playlist');
      }
    } catch (error) {
      console.error('Error fetching playlist:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to fetch playlist. Please check the URL.';
      toast.error(errorMsg);
    } finally {
      setPlaylistLoading(false);
    }
  };

  const formatSongListText = () => {
    const title = selectedPlaylist 
      ? `${selectedPlaylist.name}\nby ${selectedPlaylist.owner}\n` 
      : 'Song List\n';
    
    const separator = '='.repeat(50);
    const date = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    let text = `${title}\n${separator}\n`;
    text += `Generated: ${date}\n`;
    text += `Total Tracks: ${webSearchResults.length}\n${separator}\n\n`;
    
    webSearchResults.forEach((track, idx) => {
      const number = `${idx + 1}.`.padEnd(4);
      const timestamp = track.timestamp ? `[${track.timestamp}] ` : '';
      text += `${number}${timestamp}${track.artist} - ${track.title}\n`;
      
      if (track.album) {
        text += `      Album: ${track.album}\n`;
      }
      if (track.bpm || track.camelot_key || track.key) {
        const details = [];
        if (track.bpm) details.push(`${track.bpm} BPM`);
        if (track.camelot_key) details.push(track.camelot_key);
        else if (track.key) details.push(track.key);
        text += `      ${details.join(' | ')}\n`;
      }
      if (track.genre) {
        text += `      Genre: ${track.genre}\n`;
      }
      text += '\n';
    });
    
    text += `\n${separator}\n`;
    text += `Generated by DJ Harmonic Mixer\n`;
    
    return text;
  };

  const copySongListToClipboard = () => {
    const text = formatSongListText();
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          toast.success('Song list copied to clipboard!');
        })
        .catch(() => {
          toast.error('Failed to copy. Please try manually.');
        });
    } else {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Song list copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy');
      }
      document.body.removeChild(textArea);
    }
  };

  const exportToPDF = async () => {
    if (webSearchResults.length === 0) {
      toast.error('No songs to export');
      return;
    }
    
    try {
      toast.loading('Generating PDF...');
      const response = await axios.post(`${API}/export/songlist`, webSearchResults, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `songlist_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success(`Exported ${webSearchResults.length} songs to PDF!`);
      setShowSongListModal(false);
    } catch (error) {
      toast.dismiss();
      toast.error('Error exporting PDF');
    }
  };

  const playPreview = (previewUrl, trackId) => {
    // Stop current preview if playing
    if (audioPlayer) {
      audioPlayer.pause();
    }
    
    if (playingPreview === trackId) {
      setPlayingPreview(null);
      setAudioPlayer(null);
    } else {
      const audio = new Audio(previewUrl);
      audio.play();
      setAudioPlayer(audio);
      setPlayingPreview(trackId);
      
      audio.onended = () => {
        setPlayingPreview(null);
        setAudioPlayer(null);
      };
    }
  };

  const handleWebTrackSelect = async (track) => {
    setSelectedWebTrack(track);
    
    // Check if this is a YouTube video and try to extract tracklist
    if (track.youtube_url && track.youtube_url.includes('youtube.com/watch')) {
      try {
        const videoId = new URL(track.youtube_url).searchParams.get('v');
        if (videoId) {
          const tracklistResponse = await axios.get(`${API}/youtube/video/${videoId}/tracklist`);
          if (tracklistResponse.data.has_tracklist && tracklistResponse.data.tracks.length > 0) {
            // Save current search results before switching to tracklist
            setPreviousSearchResults(webSearchResults);
            
            // Display tracklist in sidebar instead of suggestions
            setWebSearchResults(tracklistResponse.data.tracks);
            setSelectedPlaylist({
              name: `${track.title} - Tracklist`,
              owner: track.artist,
              tracks_count: tracklistResponse.data.tracks.length,
              image_url: track.artwork_url,
              spotify_url: track.youtube_url,
              type: 'youtube_tracklist'
            });
            toast.success(`Found ${tracklistResponse.data.tracks.length} tracks in description!`);
            return;
          }
        }
      } catch (err) {
        console.log('No tracklist found in description');
      }
    }
    
    if (tracks.length === 0) {
      setLocalSuggestions([]);
      return;
    }
    
    try {
      let compatible = [];
      
      // Strategy 1: BPM + Key matching (most precise)
      if (track.bpm && track.camelot_key) {
        const minBpm = track.bpm * 0.90;
        const maxBpm = track.bpm * 1.10;
        const number = parseInt(track.camelot_key);
        const letter = track.camelot_key.slice(-1);
        
        const compatibleKeys = [
          track.camelot_key,
          `${(number % 12) + 1}${letter}`,
          `${((number - 2) % 12) + 1}${letter}`,
          `${number}${letter === 'A' ? 'B' : 'A'}`
        ];
        
        compatible = tracks.filter(t => 
          t.bpm >= minBpm && t.bpm <= maxBpm && 
          t.camelot_key && compatibleKeys.includes(t.camelot_key)
        );
      }
      
      // Strategy 2: BPM only (if no key or no key matches)
      if (compatible.length === 0 && track.bpm) {
        const minBpm = track.bpm * 0.90;
        const maxBpm = track.bpm * 1.10;
        compatible = tracks.filter(t => t.bpm >= minBpm && t.bpm <= maxBpm);
      }
      
      // Strategy 3: Genre matching (fallback)
      if (compatible.length === 0 && track.genre) {
        compatible = tracks.filter(t => 
          t.genre && t.genre.toLowerCase().includes(track.genre.toLowerCase())
        );
      }
      
      // Strategy 4: Artist similarity (last resort)
      if (compatible.length === 0) {
        const webArtist = track.artist.toLowerCase();
        compatible = tracks.filter(t => 
          t.artist.toLowerCase().includes(webArtist) || 
          webArtist.includes(t.artist.toLowerCase())
        );
      }
      
      // Sort by relevance (BPM closeness if available)
      if (track.bpm) {
        compatible.sort((a, b) => {
          const aDiff = Math.abs(a.bpm - track.bpm);
          const bDiff = Math.abs(b.bpm - track.bpm);
          return aDiff - bDiff;
        });
      }
      
      setLocalSuggestions(compatible.slice(0, 15));
      
      if (compatible.length > 0) {
        toast.success(`Found ${compatible.length} compatible tracks!`);
      } else {
        toast.info('No similar tracks found in your library');
      }
    } catch (error) {
      console.error('Error finding compatible tracks:', error);
      setLocalSuggestions([]);
    }
  };

  const filteredTracks = tracks.filter(track => {
    const search = searchTerm.toLowerCase();
    return (
      track.title.toLowerCase().includes(search) ||
      track.artist.toLowerCase().includes(search) ||
      (track.genre && track.genre.toLowerCase().includes(search))
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      <div className="border-b border-cyan-900/30 bg-black/60 backdrop-blur-xl shadow-xl shadow-cyan-500/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-cyan-500/30 to-purple-500/30 rounded-xl shadow-lg shadow-cyan-500/20">
                <Disc3 className="w-8 h-8 text-cyan-300 animate-pulse" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">DJ Harmonic Mixer</h1>
                <p className="text-sm text-gray-400 font-medium">Find perfect track combinations</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Admin Button - Only show for admin users */}
              {(() => {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                return user.is_admin ? (
                  <Button
                    onClick={() => window.location.href = '/admin'}
                    className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold px-6"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Button>
                ) : null;
              })()}
              
              <Button
                onClick={() => window.location.href = '/library'}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold px-6"
              >
                <Folder className="w-4 h-4 mr-2" />
                Library Manager
              </Button>
              <Button
                onClick={() => window.location.href = '/mixer'}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold shadow-lg"
              >
                🎚️ Open DJ Mixer
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                size="sm" 
                variant="destructive"
                onClick={async () => {
                  if (confirm('Delete ALL tracks? This will permanently remove all files and database entries.')) {
                    try {
                      toast.loading('Deleting all tracks...');
                      await axios.delete(`${API}/library/tracks/all`);
                      toast.dismiss();
                      toast.success('All tracks deleted successfully!');
                      
                      // Refresh tracks and stats
                      setTracks([]);
                      loadStats();
                    } catch (error) {
                      toast.dismiss();
                      toast.error('Failed to delete all tracks');
                      console.error('Delete all error:', error);
                    }
                  }
                }}
                className="bg-red-700 hover:bg-red-800 font-semibold shadow-md"
                data-testid="delete-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All
              </Button>
              <Dialog open={showAddTrack} onOpenChange={setShowAddTrack}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 font-semibold shadow-md" data-testid="add-track-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Upload Tracks
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-gradient-to-br from-gray-900 to-gray-950 border-cyan-700/30 max-w-2xl" data-testid="add-track-modal">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                      🎵 Upload Audio Files
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Upload MP3, WAV, FLAC, or M4A files. BPM and key will be automatically detected!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* Upload Options */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="border-2 border-dashed border-cyan-700/50 rounded-lg p-6 text-center hover:border-cyan-600/70 transition-all bg-gray-800/30 cursor-pointer"
                        onClick={() => document.getElementById('audio-upload-only').click()}
                      >
                        <input
                          type="file"
                          id="audio-upload-only"
                          multiple
                          webkitdirectory=""
                          directory=""
                          accept=".mp3,.wav,.flac,.m4a,audio/*"
                          className="hidden"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            if (files.length === 0) return;
                            
                            setUploadingFiles(true);
                            
                            try {
                              const formData = new FormData();
                              files.forEach(file => formData.append('files', file));
                              
                              toast.loading(`Uploading ${files.length} file(s)...`);
                              
                              const token = localStorage.getItem('token');
                              const response = await axios.post(`${API}/library/upload/multiple?scan=false`, formData, {
                                headers: { 
                                  'Content-Type': 'multipart/form-data',
                                  'Authorization': `Bearer ${token}`
                                }
                              });
                              
                              toast.dismiss();
                              toast.success(`✅ Uploaded ${response.data.total_uploaded} tracks (not scanned)`);
                              
                              const tracksResponse = await axios.get(`${API}/tracks`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setTracks(tracksResponse.data);
                              setShowAddTrack(false);
                              
                            } catch (error) {
                              toast.dismiss();
                              toast.error('Failed to upload files');
                            } finally {
                              setUploadingFiles(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <Music className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                        <p className="text-white font-semibold text-sm">📁 Upload Only</p>
                        <p className="text-xs text-gray-400 mt-1">Fast upload, scan later</p>
                      </div>
                      
                      <div className="border-2 border-dashed border-green-700/50 rounded-lg p-6 text-center hover:border-green-600/70 transition-all bg-gray-800/30 cursor-pointer"
                        onClick={() => document.getElementById('audio-upload-scan').click()}
                      >
                        <input
                          type="file"
                          id="audio-upload-scan"
                          multiple
                          webkitdirectory=""
                          directory=""
                          accept=".mp3,.wav,.flac,.m4a,audio/*"
                          className="hidden"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            if (files.length === 0) return;
                            
                            setUploadingFiles(true);
                            
                            try {
                              const formData = new FormData();
                              files.forEach(file => formData.append('files', file));
                              
                              toast.loading(`Uploading and scanning ${files.length} file(s)...`);
                              
                              const token = localStorage.getItem('token');
                              const response = await axios.post(`${API}/library/upload/multiple?scan=true`, formData, {
                                headers: { 
                                  'Content-Type': 'multipart/form-data',
                                  'Authorization': `Bearer ${token}`
                                }
                              });
                              
                              toast.dismiss();
                              toast.success(`✅ Uploaded & scanned ${response.data.total_uploaded} tracks!`);
                              
                              const tracksResponse = await axios.get(`${API}/tracks`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setTracks(tracksResponse.data);
                              setShowAddTrack(false);
                              
                            } catch (error) {
                              toast.dismiss();
                              toast.error('Failed to upload files');
                            } finally {
                              setUploadingFiles(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <Music className="w-12 h-12 text-green-400 mx-auto mb-2" />
                        <p className="text-white font-semibold text-sm">🔍 Upload & Scan</p>
                        <p className="text-xs text-gray-400 mt-1">Auto-detect BPM/Key</p>
                      </div>
                    </div>
                    
                    <div className="text-center text-gray-500 text-xs">
                      💡 Select files or folders to upload
                    </div>
                    
                    {uploadingFiles && (
                      <div className="bg-gray-800/50 rounded-lg p-4 border border-cyan-700/30">
                        <div className="flex items-center gap-3">
                          <div className="animate-spin w-6 h-6 border-4 border-cyan-600 border-t-transparent rounded-full" />
                          <div>
                            <p className="text-white font-semibold">Processing files...</p>
                            <p className="text-sm text-gray-400">Analyzing audio and detecting BPM/key</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-4">
                      <h4 className="text-cyan-400 font-semibold mb-2">✨ Auto-Detection Features:</h4>
                      <ul className="text-sm text-gray-300 space-y-1">
                        <li>• BPM (Beats Per Minute) detection</li>
                        <li>• Musical key identification</li>
                        <li>• Camelot wheel conversion</li>
                        <li>• Metadata extraction (title, artist, genre)</li>
                      </ul>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {stats && (
        <div className="border-b border-gray-800/50 bg-gradient-to-r from-gray-950/50 via-cyan-950/20 to-gray-950/50">
          <div className="container mx-auto px-6 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-cyan-900/20 to-cyan-950/20 border border-cyan-700/20">
                <p className="text-3xl font-bold text-cyan-400 drop-shadow-lg" data-testid="total-tracks-count">{stats.total_tracks}</p>
                <p className="text-sm text-gray-400 font-medium">Total Tracks</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-purple-900/20 to-purple-950/20 border border-purple-700/20">
                <p className="text-3xl font-bold text-purple-400 drop-shadow-lg" data-testid="favorites-count">{stats.total_favorites}</p>
                <p className="text-sm text-gray-400 font-medium">Favorites</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-green-900/20 to-green-950/20 border border-green-700/20">
                <p className="text-3xl font-bold text-green-400 drop-shadow-lg" data-testid="scans-count">{stats.total_scans}</p>
                <p className="text-sm text-gray-400 font-medium">Library Scans</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-orange-900/20 to-orange-950/20 border border-orange-700/20">
                <p className="text-3xl font-bold text-orange-400 drop-shadow-lg" data-testid="genres-count">{stats.genres.length}</p>
                <p className="text-sm text-gray-400 font-medium">Genres</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 py-8">
        <Tabs defaultValue="mixer" className="space-y-6">
          <TabsList className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 border border-cyan-700/30 p-1 shadow-lg">
            <TabsTrigger 
              value="mixer" 
              data-testid="mixer-tab" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/50 font-semibold transition-all"
            >
              🎚️ Harmonic Mixer
            </TabsTrigger>
            <TabsTrigger 
              value="websearch" 
              data-testid="websearch-tab" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-500 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-purple-500/50 font-semibold transition-all"
            >
              🌐 Web Search
            </TabsTrigger>
            <TabsTrigger 
              value="playlisturl" 
              data-testid="playlisturl-tab" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-600 data-[state=active]:to-amber-500 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-orange-500/50 font-semibold transition-all"
            >
              🔗 Playlist URL
            </TabsTrigger>
            <TabsTrigger 
              value="favorites" 
              data-testid="favorites-tab" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-600 data-[state=active]:to-rose-500 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-pink-500/50 font-semibold transition-all"
            >
              ❤️ Favorites
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mixer" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border-cyan-700/30 shadow-xl shadow-cyan-500/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 font-bold">
                    <Music className="w-5 h-5 text-cyan-400" />
                    Your Library
                  </CardTitle>
                  <CardDescription className="text-gray-400">Select a track to find compatible mixes</CardDescription>
                  
                  <div className="mt-4 space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                      <Input
                        data-testid="search-tracks-input"
                        placeholder="Search tracks, artists, genre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          toast.loading('Scanning all unscanned tracks...');
                          const response = await axios.post(`${API}/library/scan/all`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.dismiss();
                          toast.success(`✅ Scanned ${response.data.total_scanned} tracks!`);
                          
                          // Refresh tracks
                          const tracksResponse = await axios.get(`${API}/tracks`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setTracks(tracksResponse.data);
                        } catch (error) {
                          toast.dismiss();
                          toast.error('Failed to scan tracks');
                        }
                      }}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 font-semibold shadow-md"
                    >
                      🔍 Scan All Unscanned Tracks
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] pr-4">
                    {filteredTracks.length === 0 ? (
                      <div className="text-center py-12" data-testid="no-tracks-message">
                        <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No tracks found</p>
                        <p className="text-sm text-gray-500 mt-1">Add tracks or load folders from Library Manager</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredTracks.map((track) => (
                          <div
                            key={track.id}
                            data-testid={`track-item-${track.id}`}
                            onClick={() => handleTrackSelect(track)}
                            className={`p-4 rounded-lg cursor-pointer border transition-all transform hover:scale-[1.02] track-card ${
                              selectedTrack?.id === track.id
                                ? 'bg-gradient-to-r from-cyan-600/30 to-blue-600/30 border-cyan-500 shadow-lg shadow-cyan-500/30'
                                : 'bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-gray-700 hover:border-cyan-600/50 hover:shadow-md hover:shadow-cyan-500/20'
                            }`}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-white">{track.title}</h4>
                                <p className="text-sm text-gray-400">{track.artist}</p>
                                {track.genre && (
                                  <p className="text-xs text-gray-400 mt-1 font-medium">🎵 {track.genre}</p>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 items-end">
                                <div className="flex gap-2">
                                  {track.bpm ? (
                                    <Badge variant="outline" className="bg-gradient-to-r from-cyan-600/30 to-cyan-500/30 text-cyan-300 border-cyan-500/50 font-semibold">
                                      {track.bpm} BPM
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="bg-gray-700/30 text-gray-400 border-gray-600/50 text-xs">
                                      Not scanned
                                    </Badge>
                                  )}
                                  {track.camelot_key && (
                                    <Badge variant="outline" className="bg-gradient-to-r from-purple-600/30 to-purple-500/30 text-purple-300 border-purple-500/50 font-semibold">
                                      {track.camelot_key}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const audio = new Audio(`${API}/library/audio/${track.id}`);
                                      if (playingPreview === track.id) {
                                        audio.pause();
                                        setPlayingPreview(null);
                                      } else {
                                        if (audioPlayer) audioPlayer.pause();
                                        audio.play();
                                        setAudioPlayer(audio);
                                        setPlayingPreview(track.id);
                                        audio.onended = () => setPlayingPreview(null);
                                      }
                                    }}
                                    className={`${playingPreview === track.id ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-semibold text-xs shadow-md`}
                                  >
                                    {playingPreview === track.id ? '⏹️ Stop' : '▶️ Play'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (confirm(`Delete "${track.title}"? This will permanently remove the file and database entry.`)) {
                                        try {
                                          const token = localStorage.getItem('token');
                                          toast.loading('Deleting track...');
                                          await axios.delete(`${API}/library/track/${track.id}`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          toast.dismiss();
                                          toast.success('Track deleted successfully!');
                                          
                                          // Refresh tracks
                                          const response = await axios.get(`${API}/tracks`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          setTracks(response.data);
                                        } catch (error) {
                                          toast.dismiss();
                                          toast.error('Failed to delete track');
                                          console.error('Delete error:', error);
                                        }
                                      }
                                    }}
                                    className="bg-red-700 hover:bg-red-800 text-white font-semibold text-xs shadow-md"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border-purple-700/30 shadow-xl shadow-purple-500/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 font-bold">
                    <Filter className="w-5 h-5 text-purple-400" />
                    Compatible Tracks
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    {selectedTrack ? `Mixing with: ${selectedTrack.title}` : 'Select a track to see compatible mixes'}
                  </CardDescription>
                  
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                      <Label className="text-gray-400 text-xs">BPM Match</Label>
                      <Select value={bpmFilter} onValueChange={setBpmFilter}>
                        <SelectTrigger data-testid="bpm-filter-select" className="bg-gray-800 border-gray-700 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="exact">Exact</SelectItem>
                          <SelectItem value="5percent">±5%</SelectItem>
                          <SelectItem value="10percent">±10%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs">Key Match</Label>
                      <Select value={keyFilter} onValueChange={setKeyFilter}>
                        <SelectTrigger data-testid="key-filter-select" className="bg-gray-800 border-gray-700 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="all">All Compatible</SelectItem>
                          <SelectItem value="perfect">Perfect Match</SelectItem>
                          <SelectItem value="energy_up">Energy Up</SelectItem>
                          <SelectItem value="energy_down">Energy Down</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {selectedTrack && (
                    <Button 
                      data-testid="refresh-compatible-btn"
                      onClick={() => handleTrackSelect(selectedTrack)}
                      disabled={loading}
                      className="w-full mt-3 bg-gray-700 hover:bg-gray-600"
                      size="sm"
                    >
                      Refresh Results
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] pr-4">
                    {!selectedTrack ? (
                      <div className="text-center py-12" data-testid="select-track-message">
                        <Disc3 className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse-slow" />
                        <p className="text-gray-400">Select a track from your library</p>
                        <p className="text-sm text-gray-500 mt-1">to find compatible mixes</p>
                      </div>
                    ) : loading ? (
                      <div className="text-center py-12" data-testid="loading-message">
                        <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto" />
                        <p className="text-gray-400 mt-3">Finding compatible tracks...</p>
                      </div>
                    ) : compatibleTracks.length === 0 ? (
                      <div className="text-center py-12" data-testid="no-compatible-message">
                        <p className="text-gray-400">No compatible tracks found</p>
                        <p className="text-sm text-gray-500 mt-1">Try adjusting the filters</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {compatibleTracks.map((track) => (
                          <div
                            key={track.id}
                            data-testid={`compatible-track-${track.id}`}
                            className="p-4 rounded-lg bg-gray-800/50 border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-all group"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-white">{track.title}</h4>
                                <p className="text-sm text-gray-400">{track.artist}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-cyan-600/20 text-cyan-400 border-cyan-600">
                                  {track.bpm} BPM
                                </Badge>
                                {track.camelot_key && (
                                  <Badge variant="outline" className="bg-purple-600/20 text-purple-400 border-purple-600">
                                    {track.camelot_key}
                                  </Badge>
                                )}
                                <Button
                                  data-testid={`save-favorite-${track.id}`}
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleSaveFavorite(track)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Heart className="w-4 h-4 text-pink-500" />
                                </Button>
                              </div>
                            </div>
                            {track.genre && (
                              <p className="text-xs text-gray-500 mt-2">{track.genre}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="websearch" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
            <Card className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border-pink-700/30 shadow-xl shadow-pink-500/5">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2 font-bold">
                    <Search className="w-5 h-5 text-pink-400" />
                    Search Songs from Web
                  </CardTitle>
                  {webSearchResults.length > 0 && (
                    <Button
                      data-testid="export-pdf-btn"
                      onClick={() => setShowSongListModal(true)}
                      size="sm"
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 font-semibold shadow-md"
                    >
                      📄 Song List
                    </Button>
                  )}
                </div>
                <CardDescription className="text-gray-400">
                  Find songs by genre, occasion, or artist - powered by Spotify & iTunes
                </CardDescription>
                
                {(selectedGenre !== 'all' || selectedYear !== 'all') && (
                  <div className="flex gap-2 mt-2">
                    {selectedGenre !== 'all' && (
                      <Badge className="bg-cyan-600 text-white">
                        Genre: {selectedGenre}
                      </Badge>
                    )}
                    {selectedYear !== 'all' && (
                      <Badge className="bg-purple-600 text-white">
                        Year: {selectedYear === 'custom' ? customYear || 'Custom' : selectedYear}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setSelectedGenre('all'); setSelectedYear('all'); setCustomYear(''); }}
                      className="h-6 text-xs"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
                
                <div className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Select value={searchSource} onValueChange={setSearchSource}>
                      <SelectTrigger data-testid="source-select" className="bg-gray-800 border-gray-700 text-white w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="spotify">Spotify</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="itunes">iTunes</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {(searchSource === 'spotify' || searchSource === 'youtube') && (
                      <Select value={searchType} onValueChange={setSearchType}>
                        <SelectTrigger data-testid="type-select" className="bg-gray-800 border-gray-700 text-white w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="tracks">Tracks</SelectItem>
                          <SelectItem value="playlists">Playlists</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    
                    <Select value={selectedGenre} onValueChange={setSelectedGenre}>
                      <SelectTrigger data-testid="genre-select" className="bg-gray-800 border-gray-700 text-white w-36">
                        <SelectValue placeholder="Genre" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 max-h-[400px] overflow-y-auto">
                        <SelectItem value="all">All Genres</SelectItem>
                        <SelectItem value="hiphop">Hip Hop</SelectItem>
                        <SelectItem value="drill">Drill</SelectItem>
                        <SelectItem value="trap">Trap</SelectItem>
                        <SelectItem value="rnb">R&B</SelectItem>
                        <SelectItem value="soul">Soul</SelectItem>
                        <SelectItem value="funk">Funk</SelectItem>
                        <SelectItem value="dancehall">Dancehall</SelectItem>
                        <SelectItem value="reggae">Reggae</SelectItem>
                        <SelectItem value="reggaeton">Reggaeton</SelectItem>
                        <SelectItem value="soca">Soca</SelectItem>
                        <SelectItem value="afrobeats">Afrobeats</SelectItem>
                        <SelectItem value="amapiano">Amapiano</SelectItem>
                        <SelectItem value="edm">EDM</SelectItem>
                        <SelectItem value="house">House</SelectItem>
                        <SelectItem value="techno">Techno</SelectItem>
                        <SelectItem value="trance">Trance</SelectItem>
                        <SelectItem value="drum and bass">Drum & Bass</SelectItem>
                        <SelectItem value="dubstep">Dubstep</SelectItem>
                        <SelectItem value="garage">UK Garage</SelectItem>
                        <SelectItem value="grime">Grime</SelectItem>
                        <SelectItem value="latin">Latin</SelectItem>
                        <SelectItem value="salsa">Salsa</SelectItem>
                        <SelectItem value="bachata">Bachata</SelectItem>
                        <SelectItem value="merengue">Merengue</SelectItem>
                        <SelectItem value="pop">Pop</SelectItem>
                        <SelectItem value="rock">Rock</SelectItem>
                        <SelectItem value="indie">Indie</SelectItem>
                        <SelectItem value="alternative">Alternative</SelectItem>
                        <SelectItem value="country">Country</SelectItem>
                        <SelectItem value="jazz">Jazz</SelectItem>
                        <SelectItem value="blues">Blues</SelectItem>
                        <SelectItem value="gospel">Gospel</SelectItem>
                        <SelectItem value="metal">Metal</SelectItem>
                        <SelectItem value="punk">Punk</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                      <SelectTrigger data-testid="year-select" className="bg-gray-800 border-gray-700 text-white w-32">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="all">All Years</SelectItem>
                        <SelectItem value="2026">2026</SelectItem>
                        <SelectItem value="2025">2025</SelectItem>
                        <SelectItem value="2024">2024</SelectItem>
                        <SelectItem value="2023">2023</SelectItem>
                        <SelectItem value="2022">2022</SelectItem>
                        <SelectItem value="2021">2021</SelectItem>
                        <SelectItem value="2020">2020</SelectItem>
                        <SelectItem value="custom">Custom...</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {selectedYear === 'custom' && (
                      <Input
                        placeholder="Enter year (e.g., 2019)"
                        value={customYear}
                        onChange={(e) => setCustomYear(e.target.value)}
                        className="bg-gray-800 border-gray-700 text-white w-32"
                        type="number"
                        min="1900"
                        max="2099"
                      />
                    )}
                    
                    <Input
                      data-testid="web-search-input"
                      placeholder={searchType === 'playlists' ? "Search playlists..." : "Type anything: artist, song, genre, year... (e.g., 'masicka', 'dancehall 2024', 'jimmy cliff')"}
                      value={webSearchQuery}
                      onChange={(e) => setWebSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleWebSearch()}
                      className="bg-gray-800 border-gray-700 text-white flex-1"
                    />
                    <Button 
                      data-testid="web-search-btn"
                      onClick={() => handleWebSearch()}
                      disabled={webSearchLoading}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {webSearchLoading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <p className="text-xs font-semibold text-gray-300 w-full mb-1 flex items-center gap-2">
                        <span className="text-lg">🔥</span> TRENDING CHARTS
                      </p>
                      <Button
                        data-testid="trending-all-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('all')}
                        className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-pink-500/50 transition-all"
                      >
                        🔥 Top Hits
                      </Button>
                      <Button
                        data-testid="trending-hiphop-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('hiphop')}
                        className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold shadow-lg hover:shadow-orange-500/50 transition-all"
                      >
                        🎤 Hip Hop
                      </Button>
                      <Button
                        data-testid="trending-rnb-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('rnb')}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold shadow-lg hover:shadow-purple-500/50 transition-all"
                      >
                        💜 R&B
                      </Button>
                      <Button
                        data-testid="trending-dancehall-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('dancehall')}
                        className="bg-gradient-to-r from-green-600 to-yellow-500 hover:from-green-700 hover:to-yellow-600 text-white font-semibold shadow-lg hover:shadow-green-500/50 transition-all"
                      >
                        🇯🇲 Dancehall
                      </Button>
                      <Button
                        data-testid="trending-reggaeton-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('reggaeton')}
                        className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white font-semibold shadow-lg hover:shadow-red-500/50 transition-all"
                      >
                        🔊 Reggaeton
                      </Button>
                      <Button
                        data-testid="trending-afrobeats-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('afrobeats')}
                        className="bg-gradient-to-r from-amber-600 to-green-600 hover:from-amber-700 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-amber-500/50 transition-all"
                      >
                        🌍 Afrobeats
                      </Button>
                      <Button
                        data-testid="trending-edm-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('edm')}
                        className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg hover:shadow-cyan-500/50 transition-all"
                      >
                        ⚡ EDM
                      </Button>
                      <Button
                        data-testid="trending-latin-btn"
                        size="sm"
                        onClick={() => handleTrendingSearch('latin')}
                        className="bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-700 hover:to-orange-600 text-white font-semibold shadow-lg hover:shadow-rose-500/50 transition-all"
                      >
                        🎺 Latin
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('house')}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-indigo-500/50 transition-all"
                      >
                        🏠 House
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('trap')}
                        className="bg-gradient-to-r from-gray-700 to-red-700 hover:from-gray-800 hover:to-red-800 text-white font-semibold shadow-lg hover:shadow-red-500/50 transition-all"
                      >
                        🎹 Trap
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('drill')}
                        className="bg-gradient-to-r from-slate-700 to-blue-700 hover:from-slate-800 hover:to-blue-800 text-white font-semibold shadow-lg hover:shadow-blue-500/50 transition-all"
                      >
                        🔫 Drill
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('soca')}
                        className="bg-gradient-to-r from-yellow-500 to-red-500 hover:from-yellow-600 hover:to-red-600 text-white font-semibold shadow-lg hover:shadow-yellow-500/50 transition-all"
                      >
                        🇹🇹 Soca
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('amapiano')}
                        className="bg-gradient-to-r from-orange-500 to-yellow-600 hover:from-orange-600 hover:to-yellow-700 text-white font-semibold shadow-lg hover:shadow-orange-500/50 transition-all"
                      >
                        🇿🇦 Amapiano
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('drum and bass')}
                        className="bg-gradient-to-r from-blue-700 to-purple-700 hover:from-blue-800 hover:to-purple-800 text-white font-semibold shadow-lg hover:shadow-blue-500/50 transition-all"
                      >
                        🥁 Drum & Bass
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('reggae')}
                        className="bg-gradient-to-r from-green-700 to-yellow-600 hover:from-green-800 hover:to-yellow-700 text-white font-semibold shadow-lg hover:shadow-green-500/50 transition-all"
                      >
                        🇯🇲 Reggae
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('techno')}
                        className="bg-gradient-to-r from-gray-600 to-cyan-600 hover:from-gray-700 hover:to-cyan-700 text-white font-semibold shadow-lg hover:shadow-cyan-500/50 transition-all"
                      >
                        🤖 Techno
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('uk garage')}
                        className="bg-gradient-to-r from-slate-600 to-blue-600 hover:from-slate-700 hover:to-blue-700 text-white font-semibold shadow-lg hover:shadow-blue-500/50 transition-all"
                      >
                        🇬🇧 UK Garage
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('pop')}
                        className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold shadow-lg hover:shadow-pink-500/50 transition-all"
                      >
                        🎤 Pop
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleTrendingSearch('country')}
                        className="bg-gradient-to-r from-amber-700 to-orange-700 hover:from-amber-800 hover:to-orange-800 text-white font-semibold shadow-lg hover:shadow-amber-500/50 transition-all"
                      >
                        🤠 Country
                      </Button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <p className="text-xs font-semibold text-gray-300 w-full mb-1 flex items-center gap-2">
                        <span className="text-lg">🎉</span> QUICK CATEGORIES
                      </p>
                      <Button
                        data-testid="category-wedding-btn"
                        size="sm"
                        onClick={() => handleCuratedSearch('wedding')}
                        className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white border-0 font-semibold shadow-md hover:shadow-pink-400/50 transition-all"
                      >
                        💒 Wedding
                      </Button>
                      <Button
                        data-testid="category-club-btn"
                        size="sm"
                        onClick={() => handleCuratedSearch('club')}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0 font-semibold shadow-md hover:shadow-purple-400/50 transition-all"
                      >
                        🎪 Club
                      </Button>
                      <Button
                        data-testid="category-top40-btn"
                        size="sm"
                        onClick={() => handleCuratedSearch('top40')}
                        className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white border-0 font-semibold shadow-md hover:shadow-yellow-400/50 transition-all"
                      >
                        📊 Top40
                      </Button>
                      <Button
                        data-testid="category-party-btn"
                        size="sm"
                        onClick={() => handleCuratedSearch('party')}
                        className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white border-0 font-semibold shadow-md hover:shadow-green-400/50 transition-all"
                      >
                        🎊 Party
                      </Button>
                      <Button
                        data-testid="category-throwback-btn"
                        size="sm"
                        onClick={() => handleCuratedSearch('throwback')}
                        className="bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-700 hover:to-red-700 text-white border-0 font-semibold shadow-md hover:shadow-amber-400/50 transition-all"
                      >
                        ⏮️ Throwback
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('workout')}
                        className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white border-0 font-semibold shadow-md hover:shadow-red-400/50 transition-all"
                      >
                        💪 Workout
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('chill')}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 font-semibold shadow-md hover:shadow-blue-400/50 transition-all"
                      >
                        😌 Chill
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('beach')}
                        className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white border-0 font-semibold shadow-md hover:shadow-cyan-400/50 transition-all"
                      >
                        🏖️ Beach
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('oldschool')}
                        className="bg-gradient-to-r from-orange-700 to-amber-700 hover:from-orange-800 hover:to-amber-800 text-white border-0 font-semibold shadow-md hover:shadow-orange-400/50 transition-all"
                      >
                        📻 Old School
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('east coast rap')}
                        className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white border-0 font-semibold shadow-md hover:shadow-blue-400/50 transition-all"
                      >
                        🗽 East Coast Rap
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('west coast rap')}
                        className="bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white border-0 font-semibold shadow-md hover:shadow-orange-400/50 transition-all"
                      >
                        🌴 West Coast Rap
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('80s - 90s hip hop')}
                        className="bg-gradient-to-r from-purple-700 to-red-700 hover:from-purple-800 hover:to-red-800 text-white border-0 font-semibold shadow-md hover:shadow-purple-400/50 transition-all"
                      >
                        📼 80s - 90s Hip Hop
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('latin urban')}
                        className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white border-0 font-semibold shadow-md hover:shadow-red-400/50 transition-all"
                      >
                        🔥 Latin Urban
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('caribbean')}
                        className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white border-0 font-semibold shadow-md hover:shadow-teal-400/50 transition-all"
                      >
                        🏝️ Caribbean
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('summer vibes')}
                        className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white border-0 font-semibold shadow-md hover:shadow-yellow-400/50 transition-all"
                      >
                        ☀️ Summer Vibes
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('night club')}
                        className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white border-0 font-semibold shadow-md hover:shadow-violet-400/50 transition-all"
                      >
                        🌃 Night Club
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('baile funk')}
                        className="bg-gradient-to-r from-green-600 to-lime-600 hover:from-green-700 hover:to-lime-700 text-white border-0 font-semibold shadow-md hover:shadow-green-400/50 transition-all"
                      >
                        🇧🇷 Baile Funk
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('gospel')}
                        className="bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700 text-white border-0 font-semibold shadow-md hover:shadow-yellow-400/50 transition-all"
                      >
                        🙏 Gospel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCuratedSearch('brunch')}
                        className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white border-0 font-semibold shadow-md hover:shadow-rose-400/50 transition-all"
                      >
                        🥂 Brunch
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {selectedPlaylist && (
                  <div className="mb-4 p-4 bg-cyan-900/20 border border-cyan-700/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      {selectedPlaylist.image_url && (
                        <img src={selectedPlaylist.image_url} alt={selectedPlaylist.name} className="w-12 h-12 rounded" />
                      )}
                      <div>
                        <p className="text-white font-semibold">{selectedPlaylist.name}</p>
                        <p className="text-xs text-gray-400">by {selectedPlaylist.owner} • {selectedPlaylist.tracks_count} tracks</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedPlaylist(null); setWebSearchResults([]); }}
                        className="ml-auto"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                )}
                
                <ScrollArea className="h-[600px] pr-4" onScrollCapture={handleScroll}>
                  {webSearchLoading ? (
                    <div className="text-center py-12" data-testid="web-loading">
                      <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto" />
                      <p className="text-gray-400 mt-3">Searching...</p>
                    </div>
                  ) : playlists.length > 0 ? (
                    <div className="space-y-3">
                      {playlists.map((playlist) => (
                        <div
                          key={playlist.id}
                          data-testid={`playlist-${playlist.id}`}
                          onClick={() => handlePlaylistSelect(playlist)}
                          className="p-4 rounded-lg bg-gradient-to-r from-gray-800/60 to-gray-900/60 border border-gray-700 hover:border-cyan-500/70 hover:shadow-lg hover:shadow-cyan-500/20 transition-all cursor-pointer transform hover:scale-[1.02] track-card"
                        >
                          <div className="flex gap-4">
                            {playlist.image_url && (
                              <img 
                                src={playlist.image_url} 
                                alt={playlist.name}
                                className="w-20 h-20 rounded-lg object-cover shadow-md border border-gray-700"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">{playlist.name}</h4>
                              <p className="text-sm text-gray-400 mt-1">👤 {playlist.owner}</p>
                              <p className="text-xs text-cyan-400 mt-2 font-medium">🎵 {playlist.tracks_count} tracks</p>
                              {playlist.description && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{playlist.description}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-md hover:shadow-green-500/50"
                            >
                              View Tracks
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {isLoadingMore && (
                        <div className="text-center py-4" data-testid="loading-more-playlists">
                          <div className="animate-spin w-6 h-6 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto" />
                          <p className="text-gray-400 text-sm mt-2">Loading more playlists...</p>
                        </div>
                      )}
                      
                      {!hasMore && playlists.length > 0 && !webSearchLoading && (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          End of results ({playlists.length} total playlists)
                        </div>
                      )}
                    </div>
                  ) : webSearchResults.length === 0 ? (
                    <div className="text-center py-12" data-testid="web-no-results">
                      <Search className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No results yet</p>
                      <p className="text-sm text-gray-500 mt-1">Search for songs or try a quick category</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {webSearchResults.map((track) => (
                        <div
                          key={track.id}
                          data-testid={`web-track-${track.id}`}
                          onClick={() => handleWebTrackSelect(track)}
                          className={`p-4 rounded-lg border transition-all transform hover:scale-[1.01] cursor-pointer ${
                            selectedWebTrack?.id === track.id
                              ? 'bg-gradient-to-r from-cyan-600/30 to-blue-600/30 border-cyan-500 shadow-lg shadow-cyan-500/30'
                              : 'bg-gradient-to-r from-gray-800/60 to-gray-900/60 border-gray-700 hover:border-cyan-600/50 hover:shadow-md hover:shadow-cyan-500/20'
                          }`}
                        >
                          <div className="flex gap-4">
                            {track.artwork_url && (
                              <img 
                                src={track.artwork_url} 
                                alt={track.title}
                                className="w-20 h-20 rounded-lg object-cover shadow-md border border-gray-700"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-semibold text-white text-lg">{track.title}</h4>
                              <p className="text-sm text-gray-400">{track.artist}</p>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                {track.bpm && (
                                  <Badge variant="outline" className="bg-gradient-to-r from-cyan-600/30 to-cyan-500/30 text-cyan-300 border-cyan-500/50 font-semibold">
                                    {track.bpm} BPM
                                  </Badge>
                                )}
                                {track.camelot_key && (
                                  <Badge variant="outline" className="bg-gradient-to-r from-purple-600/30 to-purple-500/30 text-purple-300 border-purple-500/50 font-semibold">
                                    {track.camelot_key}
                                  </Badge>
                                )}
                                {track.key && !track.camelot_key && (
                                  <Badge variant="outline" className="bg-gradient-to-r from-purple-600/30 to-purple-500/30 text-purple-300 border-purple-500/50 font-semibold">
                                    {track.key}
                                  </Badge>
                                )}
                                {track.genre && (
                                  <Badge variant="outline" className="bg-gradient-to-r from-orange-600/30 to-orange-500/30 text-orange-300 border-orange-500/50 font-semibold">
                                    {track.genre}
                                  </Badge>
                                )}
                                {track.duration_ms && (
                                  <Badge variant="outline" className="bg-gray-700/50 text-gray-300 border-gray-600">
                                    {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 min-w-[180px]">
                              {(track.spotify_url || track.youtube_url || track.itunes_url || track.preview_url) && (
                                <div className="mb-1">
                                  <p className="text-xs text-cyan-400 font-bold mb-2">🎧 LISTEN & BUY:</p>
                                </div>
                              )}
                              {track.preview_url && (
                                <Button
                                  data-testid={`play-preview-${track.id}`}
                                  size="sm"
                                  variant={playingPreview === track.id ? 'destructive' : 'default'}
                                  onClick={(e) => { e.stopPropagation(); playPreview(track.preview_url, track.id); }}
                                  className={`font-semibold ${playingPreview === track.id ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} shadow-md`}
                                >
                                  {playingPreview === track.id ? '⏹️ Stop' : '▶️ Preview'}
                                </Button>
                              )}
                              {track.youtube_url && (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); window.open(track.youtube_url, '_blank'); }}
                                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white border-0 font-semibold shadow-md hover:shadow-red-500/50"
                                >
                                  📺 Watch on YouTube
                                </Button>
                              )}
                              {track.spotify_url && (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); window.open(track.spotify_url, '_blank'); }}
                                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 font-semibold shadow-md hover:shadow-green-500/50"
                                >
                                  🎵 Listen on Spotify
                                </Button>
                              )}
                              {track.itunes_url && (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); window.open(track.itunes_url, '_blank'); }}
                                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 font-semibold shadow-md hover:shadow-blue-500/50"
                                >
                                  🛒 Buy on iTunes {track.price && `($${track.price})`}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {isLoadingMore && (
                        <div className="text-center py-4" data-testid="loading-more">
                          <div className="animate-spin w-6 h-6 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto" />
                          <p className="text-gray-400 text-sm mt-2">Loading more songs...</p>
                        </div>
                      )}
                      
                      {!hasMore && webSearchResults.length > 0 && !webSearchLoading && (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          End of results ({webSearchResults.length} total songs)
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
            </div>
            
            {/* Right Sidebar - Playlist Tracks or Local Library Suggestions */}
            <div className="lg:col-span-1">
              <Card className="bg-gray-900/50 border-gray-800 sticky top-4">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Music className="w-4 h-4" />
                        {selectedPlaylist ? 'Playlist Tracks' : 'Your Library Mix Suggestions'}
                      </CardTitle>
                      <CardDescription className="text-gray-400 text-sm">
                        {selectedPlaylist 
                          ? `${webSearchResults.length} tracks in this playlist` 
                          : selectedWebTrack 
                            ? 'Compatible tracks you already own' 
                            : 'Select a web track to see suggestions'}
                      </CardDescription>
                    </div>
                    {(selectedPlaylist || selectedWebTrack) && (
                      <Button
                        onClick={() => {
                          // Restore previous search results if available
                          if (previousSearchResults.length > 0) {
                            setWebSearchResults(previousSearchResults);
                            setPreviousSearchResults([]);
                          }
                          setSelectedPlaylist(null);
                          setSelectedWebTrack(null);
                          setLocalSuggestions([]);
                          toast.success('Returned to search results');
                        }}
                        size="sm"
                        className="bg-gradient-to-r from-cyan-700 to-blue-700 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold shadow-md hover:shadow-cyan-500/30 transition-all"
                      >
                        ← Back to Results
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Show playlist info when in playlist mode */}
                  {selectedPlaylist && (
                    <>
                      <div className="mb-4 p-3 bg-purple-900/20 border border-purple-700/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-gray-400">VIEWING PLAYLIST</p>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => { setSelectedPlaylist(null); setWebSearchResults([]); }}
                            className="h-6 w-6"
                          >
                            ✕
                          </Button>
                        </div>
                        <div 
                          className="flex gap-2 cursor-pointer hover:bg-purple-900/30 p-2 -m-2 rounded transition-all"
                          onClick={() => window.open(selectedPlaylist.spotify_url, '_blank')}
                          data-testid="playlist-header-link"
                        >
                          {selectedPlaylist.image_url && (
                            <img src={selectedPlaylist.image_url} alt={selectedPlaylist.name} className="w-14 h-14 rounded shadow-lg" />
                          )}
                          <div className="flex-1">
                            <p className="text-white font-semibold text-sm hover:text-cyan-400 transition-colors">{selectedPlaylist.name}</p>
                            <p className="text-xs text-gray-400">by {selectedPlaylist.owner}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-500">{selectedPlaylist.tracks_count} tracks</p>
                              <span className="text-xs text-cyan-400">→ Click to open</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Copy Playlist URL Button */}
                      <Button
                        onClick={() => {
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard.writeText(selectedPlaylist.spotify_url)
                              .then(() => {
                                toast.success('Playlist URL copied to clipboard!');
                              })
                              .catch(() => {
                                toast.error('Failed to copy URL. Please try manually.');
                              });
                          } else {
                            // Fallback for browsers that don't support clipboard API
                            const textArea = document.createElement('textarea');
                            textArea.value = selectedPlaylist.spotify_url;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.select();
                            try {
                              document.execCommand('copy');
                              toast.success('Playlist URL copied to clipboard!');
                            } catch (err) {
                              toast.error('Failed to copy. URL: ' + selectedPlaylist.spotify_url);
                            }
                            document.body.removeChild(textArea);
                          }
                        }}
                        className="w-full mb-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg hover:shadow-green-500/50 transition-all"
                        size="sm"
                      >
                        📋 Copy Playlist URL
                      </Button>
                    </>
                  )}
                  
                  {/* Show selected web track info when in track mode */}
                  {selectedWebTrack && !selectedPlaylist && (
                    <div className="mb-4 p-3 bg-cyan-900/20 border border-cyan-700/30 rounded-lg">
                      <p className="text-xs text-gray-400 mb-1">SELECTED WEB TRACK</p>
                      <div className="flex gap-2">
                        {selectedWebTrack.artwork_url && (
                          <img src={selectedWebTrack.artwork_url} alt={selectedWebTrack.title} className="w-12 h-12 rounded" />
                        )}
                        <div className="flex-1">
                          <p className="text-white font-semibold text-sm">{selectedWebTrack.title}</p>
                          <p className="text-xs text-gray-400">{selectedWebTrack.artist}</p>
                          <div className="flex gap-1 mt-1">
                            {selectedWebTrack.bpm && (
                              <Badge variant="outline" className="bg-cyan-600/20 text-cyan-400 border-cyan-600 text-xs">
                                {selectedWebTrack.bpm} BPM
                              </Badge>
                            )}
                            {selectedWebTrack.camelot_key && (
                              <Badge variant="outline" className="bg-purple-600/20 text-purple-400 border-purple-600 text-xs">
                                {selectedWebTrack.camelot_key}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => { setSelectedWebTrack(null); setLocalSuggestions([]); }}
                          className="h-8 w-8"
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <ScrollArea className="h-[500px] pr-3">
                    {/* Show playlist tracks when in playlist mode */}
                    {selectedPlaylist ? (
                      webSearchResults.length === 0 ? (
                        <div className="text-center py-12">
                          <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400 text-sm">No tracks loaded</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-400 mb-3">
                            {selectedPlaylist?.type === 'youtube_tracklist' 
                              ? `${webSearchResults.length} TRACKS IN MIX` 
                              : `${webSearchResults.length} PLAYLIST TRACKS`}
                          </p>
                          {webSearchResults.map((track, idx) => (
                            <div
                              key={track.id}
                              data-testid={`playlist-track-${track.id}`}
                              className="p-3 rounded-lg bg-gradient-to-r from-gray-800/50 to-gray-900/50 border border-gray-700 hover:border-cyan-600/50 hover:shadow-md transition-all cursor-pointer"
                              onClick={() => {
                                if (track.youtube_url) {
                                  window.open(track.youtube_url, '_blank');
                                } else {
                                  handleWebTrackSelect(track);
                                }
                              }}
                            >
                              <div className="flex gap-2 items-start">
                                {track.timestamp ? (
                                  <span className="text-cyan-400 text-xs font-bold bg-cyan-900/30 px-2 py-1 rounded">{track.timestamp}</span>
                                ) : (
                                  <span className="text-gray-500 text-xs font-bold w-6">{idx + 1}.</span>
                                )}
                                <div className="flex-1">
                                  <h4 className="font-semibold text-white text-sm">{track.title}</h4>
                                  <p className="text-xs text-gray-400">{track.artist}</p>
                                  {track.timestamp && (
                                    <p className="text-xs text-cyan-400 mt-1">🎵 Click to jump to this track</p>
                                  )}
                                  {track.preview_url && !track.timestamp && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => { e.stopPropagation(); playPreview(track.preview_url, track.id); }}
                                      className={`mt-1 h-6 text-xs ${playingPreview === track.id ? 'text-red-400' : 'text-green-400'}`}
                                    >
                                      {playingPreview === track.id ? '⏹ Stop' : '▶ Preview'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    ) : !selectedWebTrack ? (
                      <div className="text-center py-12">
                        <Disc3 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">Select a track from web search</p>
                        <p className="text-xs text-gray-500 mt-1">to see mix suggestions from your library</p>
                      </div>
                    ) : localSuggestions.length === 0 ? (
                      <div className="text-center py-12">
                        <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">No compatible tracks</p>
                        <p className="text-xs text-gray-500 mt-1">in your local library</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center mb-3">
                          <p className="text-xs text-gray-400">{localSuggestions.length} COMPATIBLE TRACKS</p>
                          <p className="text-xs text-green-400">✓ Ready to Mix</p>
                        </div>
                        {localSuggestions.map((track) => (
                          <div
                            key={track.id}
                            data-testid={`suggestion-${track.id}`}
                            className="p-3 rounded-lg bg-gray-800/50 border border-gray-700 hover:bg-gray-800 transition-all"
                          >
                            <h4 className="font-semibold text-white text-sm">{track.title}</h4>
                            <p className="text-xs text-gray-400">{track.artist}</p>
                            <div className="flex gap-1 mt-2">
                              <Badge variant="outline" className="bg-cyan-600/20 text-cyan-400 border-cyan-600 text-xs">
                                {track.bpm} BPM
                              </Badge>
                              {track.camelot_key && (
                                <Badge variant="outline" className="bg-purple-600/20 text-purple-400 border-purple-600 text-xs">
                                  {track.camelot_key}
                                </Badge>
                              )}
                              {track.genre && (
                                <Badge variant="outline" className="bg-orange-600/20 text-orange-400 border-orange-600 text-xs">
                                  {track.genre}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
            </div>
          </TabsContent>

          <TabsContent value="playlisturl">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Disc3 className="w-5 h-5 text-orange-500" />
                  Playlist URL Extractor
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Paste a YouTube playlist, channel, or mix URL to extract songs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* URL Input */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Paste YouTube playlist/channel/mix URL here..."
                      value={playlistUrl}
                      onChange={(e) => setPlaylistUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && fetchPlaylistFromUrl()}
                      className="flex-1 bg-gray-800 border-gray-700 text-white"
                    />
                    <Button
                      onClick={fetchPlaylistFromUrl}
                      disabled={playlistLoading || !playlistUrl.trim()}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      {playlistLoading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Extracting...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Extract
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Example URLs */}
                  <div className="text-xs text-gray-500">
                    <p className="mb-1">✨ Supported URLs:</p>
                    <ul className="space-y-1 ml-4">
                      <li>• Spotify Playlist: open.spotify.com/playlist/...</li>
                      <li>• YouTube Playlist: youtube.com/playlist?list=...</li>
                      <li>• YouTube Channel: youtube.com/@channelname or youtube.com/c/channelname</li>
                      <li>• YouTube Mix: youtube.com/watch?v=...&list=RD...</li>
                    </ul>
                  </div>

                  {/* Results */}
                  <ScrollArea className="h-[500px]">
                    {playlistLoading ? (
                      <div className="text-center py-12">
                        <div className="animate-spin text-4xl mb-4">⏳</div>
                        <p className="text-gray-400">Extracting playlist...</p>
                        <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
                      </div>
                    ) : playlistResults.length === 0 ? (
                      <div className="text-center py-12">
                        <Disc3 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No tracks yet</p>
                        <p className="text-sm text-gray-500 mt-1">Paste a playlist URL above to get started</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Playlist Info Header */}
                        {playlistInfo && (
                          <div className="p-4 rounded-lg bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/30 mb-4">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0">
                                {playlistInfo.source === 'spotify' ? (
                                  <div className="w-12 h-12 rounded bg-green-600 flex items-center justify-center text-2xl">
                                    🎵
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded bg-red-600 flex items-center justify-center text-2xl">
                                    ▶️
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-white text-lg">{playlistInfo.name}</h3>
                                <p className="text-sm text-gray-400">by {playlistInfo.owner}</p>
                                <div className="flex gap-2 mt-2">
                                  <Badge className={playlistInfo.source === 'spotify' ? 'bg-green-600' : 'bg-red-600'}>
                                    {playlistInfo.source === 'spotify' ? '🎵 Spotify' : '▶️ YouTube'}
                                  </Badge>
                                  <Badge className="bg-cyan-900/30 text-cyan-400">
                                    {playlistInfo.count} tracks
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center mb-4">
                          <p className="text-sm text-gray-400">
                            Found <span className="text-white font-semibold">{playlistResults.length}</span> tracks
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setWebSearchResults(playlistResults);
                              setShowSongListModal(true);
                            }}
                            className="bg-gray-800 hover:bg-gray-700 text-white border-gray-600"
                          >
                            📄 Export List
                          </Button>
                        </div>
                        
                        {playlistResults.map((track, idx) => (
                          <div
                            key={idx}
                            className="p-4 rounded-lg bg-gray-800/50 border border-gray-700 hover:border-orange-600/50 transition-all cursor-pointer"
                            onClick={() => setSelectedWebTrack(track)}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded bg-orange-600/20 flex items-center justify-center text-orange-400 font-bold text-sm">
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-white truncate">{track.title}</h4>
                                <p className="text-sm text-gray-400 truncate">{track.artist}</p>
                                {track.timestamp && (
                                  <p className="text-xs text-gray-500 mt-1">⏱️ {track.timestamp}</p>
                                )}
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {track.bpm && (
                                    <Badge className="bg-cyan-900/30 text-cyan-400 text-xs">
                                      {track.bpm} BPM
                                    </Badge>
                                  )}
                                  {track.camelot_key && (
                                    <Badge className="bg-purple-900/30 text-purple-400 text-xs">
                                      {track.camelot_key}
                                    </Badge>
                                  )}
                                  {track.genre && (
                                    <Badge className="bg-pink-900/30 text-pink-400 text-xs">
                                      {track.genre}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="favorites">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Favorite Combinations
                </CardTitle>
                <CardDescription className="text-gray-400">Your saved track combinations</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  {favorites.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-favorites-message">
                      <Heart className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No favorites yet</p>
                      <p className="text-sm text-gray-500 mt-1">Save track combinations from the mixer</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {favorites.map((fav) => (
                        <div key={fav.id} data-testid={`favorite-combo-${fav.id}`} className="p-6 rounded-lg bg-gray-800/50 border border-gray-700">
                          <div className="grid md:grid-cols-2 gap-4">
                            {fav.track1 && (
                              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                                <p className="text-xs text-gray-500 mb-2">TRACK 1</p>
                                <h4 className="font-semibold text-white">{fav.track1.title}</h4>
                                <p className="text-sm text-gray-400">{fav.track1.artist}</p>
                                <div className="flex gap-2 mt-2">
                                  <Badge variant="outline" className="bg-cyan-600/20 text-cyan-400 border-cyan-600">
                                    {fav.track1.bpm} BPM
                                  </Badge>
                                  {fav.track1.camelot_key && (
                                    <Badge variant="outline" className="bg-purple-600/20 text-purple-400 border-purple-600">
                                      {fav.track1.camelot_key}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                            {fav.track2 && (
                              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                                <p className="text-xs text-gray-500 mb-2">TRACK 2</p>
                                <h4 className="font-semibold text-white">{fav.track2.title}</h4>
                                <p className="text-sm text-gray-400">{fav.track2.artist}</p>
                                <div className="flex gap-2 mt-2">
                                  <Badge variant="outline" className="bg-cyan-600/20 text-cyan-400 border-cyan-600">
                                    {fav.track2.bpm} BPM
                                  </Badge>
                                  {fav.track2.camelot_key && (
                                    <Badge variant="outline" className="bg-purple-600/20 text-purple-400 border-purple-600">
                                      {fav.track2.camelot_key}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Song List Preview Modal */}
      <Dialog open={showSongListModal} onOpenChange={setShowSongListModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] bg-gradient-to-br from-gray-900 to-gray-950 border-cyan-700/30">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              📄 Song List Preview
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {webSearchResults.length} tracks ready to export
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preview Canvas */}
            <div className="bg-white text-black p-6 rounded-lg max-h-[400px] overflow-auto font-mono text-sm">
              <pre className="whitespace-pre-wrap">{formatSongListText()}</pre>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setShowSongListModal(false)}
                variant="outline"
                className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={copySongListToClipboard}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold shadow-lg"
              >
                📋 Copy to Clipboard
              </Button>
              <Button
                onClick={exportToPDF}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg"
              >
                📥 Download PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
